<script setup lang="ts">
import {computed, onMounted, type PropType, ref, nextTick, getCurrentInstance} from 'vue';
import {ElDialog, ElSkeleton} from 'element-plus';
import {CabKPI, CabOrganization, OnderwerpEnum, PrognoseSchaalEnum, uuidOf, WaardeTypeEnum} from 'common';
import {createObject, deleteObject, expressieDoc, listObjects, readObject, updateObject} from '@/base/callBackend';
import KpiMijlpaalStrip, {KPI_COLUMNS} from '@/views/admin/KpiMijlpaalStrip.vue';
import PageHeader from '@/views/ui/PageHeader.vue';
import CardPanel from '@/views/ui/CardPanel.vue';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import {Info, Plus} from 'lucide-vue-next';
import {KpiSelectEnum} from '@/base/cabRouter';
import GeneralFilter from '@/views/general/GeneralFilter.vue';
import MarkdownIt from 'markdown-it';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {KPI_CHANGE_EMIT, KPI_REMOVE_EMIT} from '@/base/cabConstants';

const props = defineProps({
    kpiSelect: {
        type    : Number as PropType<KpiSelectEnum>,
        required: true,
    },
});

const loading              = ref(true);
const allOrganizations     = ref<CabOrganization[]>([]);
const allKPIs              = ref<CabKPI[]>([]);
const kpis                 = computed(() => allKPIs.value.filter(k => doingMilestones() == k.isMijlpaal()));
const allKpiNames          = computed(() => allKPIs.value.flatMap(k => k.korteNaam ? [{label: k.name}, {label: k.korteNaam, detail: k.name}] : [{label: k.name}]));
const filterActive         = ref<boolean>(false);
const filteredKpis         = ref<CabKPI[]>([]);
const filterId             = computed<string>(() => doingMilestones() ? 'MilestoneList' : 'KPIList');
const featureDialogVisible = ref(false);
const content              = ref('');
const md                   = new MarkdownIt({
    linkify: true,
});

onMounted(async () => {
    allOrganizations.value = await listObjects(CabOrganization);
    allKPIs.value          = await listObjects(CabKPI);
    loading.value          = false;
});

//=====================================================================================
function doingMilestones(): boolean {
    return props.kpiSelect === KpiSelectEnum.onlyMilestones;
}

async function onKPIAdd() {
    filterActive.value   = false;
    const newKPI: CabKPI = new CabKPI();
    if (doingMilestones()) {
        newKPI.onderwerp           = OnderwerpEnum.mijlpaal;
        newKPI.waardeType          = undefined;
        newKPI.prognoseSchaal      = PrognoseSchaalEnum.once;
        newKPI.hasDoelwaarde       = false;
        newKPI.askKpiStatus        = false;
        newKPI.isCalculatedValue   = false;
        newKPI.hasUitkeringsbedrag = true;
    } else {
        newKPI.onderwerp           = OnderwerpEnum.adherentie;
        newKPI.waardeType          = WaardeTypeEnum.getal;
        newKPI.prognoseSchaal      = PrognoseSchaalEnum.none;
        newKPI.hasDoelwaarde       = false;
        newKPI.askKpiStatus        = false;
        newKPI.isCalculatedValue   = false;
        newKPI.hasUitkeringsbedrag = false;
    }
    await createObject(newKPI);
    allKPIs.value = [newKPI, ...allKPIs.value];
    await nextTick();
    getKpiStrip(makeStripRef(newKPI))?.firstEdit();
}

const instance = getCurrentInstance();

function getKpiStrip(ref: string): InstanceType<typeof KpiMijlpaalStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabKPI) {
    return `KPIStrip-${x.uuid}`;
}

async function onKPIRemove(kpi: CabKPI) {
    await deleteObject(kpi);
    allKPIs.value = allKPIs.value.filter(k => uuidOf(k) !== uuidOf(kpi));
}

async function onKPIChange(kpi: CabKPI) {
    const uuid = uuidOf(kpi);
    await updateObject(kpi);
    const kpiNew = await readObject(kpi);

    if (kpiNew) {
        const kpiIndex = allKPIs.value.findIndex((u) => uuidOf(u) === uuid);
        if (kpiIndex !== -1) {
            // this rather verbose replace of the kpi is needed to trigger vue's reactivety mechanism...
            allKPIs.value = [
                ...allKPIs.value.slice(0, kpiIndex),
                kpiNew,
                ...allKPIs.value.slice(kpiIndex + 1),
            ];
        }
    }
}

async function showInfo() {
    content.value              = await expressieDoc();
    featureDialogVisible.value = true;
}
</script>

<template>
    <div class="flex flex-col">
        <PageHeader :title="doingMilestones() ? 'Mijlpalen' : 'KPI\'s'" :context="`${filteredKpis.length} van ${kpis.length}`">
            <template #before>
                <IconButton :icon="Plus" :label="doingMilestones() ? 'nieuwe mijlpaal' : 'nieuwe KPI'" variant="primary" data-testid="plus-button" @click="onKPIAdd()"/>
            </template>
            <template #actions>
                <IconButton :icon="Info" label="expressie documentatie" data-testid="expressie-doc-button" @click="showInfo()"/>
                <GeneralFilter v-model:active="filterActive" v-model:cabList="kpis" v-model:cabListFiltered="filteredKpis" :id="filterId"/>
            </template>
        </PageHeader>
        <CardPanel min-width="52rem">
            <div v-if="loading" class="p-4">
                <el-skeleton :rows="5" animated/>
            </div>
            <template v-else>
                <RowGrid v-if="filteredKpis.length" :columns="KPI_COLUMNS" header>
                    <span/>
                    <span/>
                    <span class="pl-2">naam</span>
                    <span class="pl-[11px]">{{ doingMilestones() ? 'meetmoment' : 'onderwerp' }}</span>
                    <span>{{ doingMilestones() ? '' : 'type' }}</span>
                    <span/>
                </RowGrid>
                <div v-else class="px-4 py-6 text-sm text-zzc-muted">{{ doingMilestones() ? 'geen mijlpalen' : 'geen KPI\'s' }}</div>
                <KpiMijlpaalStrip
                    v-for="kpi in filteredKpis"
                    :key="uuidOf(kpi)"
                    :kpi="kpi"
                    :allOrganizations="allOrganizations"
                    :allKpiNames="allKpiNames"
                    :ref="makeStripRef(kpi)"
                    v-on:[KPI_REMOVE_EMIT]="onKPIRemove"
                    v-on:[KPI_CHANGE_EMIT]="onKPIChange"
                />
            </template>
        </CardPanel>
    </div>

    <el-dialog v-model="featureDialogVisible" title="Expressie documentatie" width="900px" :close-on-press-escape="true" :close-on-click-modal="true">
        <div class="prose max-w-none">
            <div v-html="md.render(content)"></div>
        </div>
    </el-dialog>
</template>

<style>
.prose h1 {
    @apply text-3xl font-bold text-zzc-blauw mt-5;
}

.prose h2 {
    @apply text-2xl font-semibold text-zzc-blauw mt-4;
}

.prose h3 {
    @apply text-xl font-medium text-zzc-blauw mt-3;
}

.prose p {
    @apply mt-2;
}

.prose ul {
    @apply my-3  mx-20;
}

.prose ol {
    @apply my-3  mx-20;
}

.prose table {
    @apply my-3  mx-20;
}

.prose th {
    @apply p-1 px-5 bg-zzc-lichtgrijs;
}

.prose td {
    @apply p-1 px-5 border-b border-zzc-rand;
}

.prose pre {
    @apply my-3 mx-10 px-3 p-1 bg-zzc-lichtgrijs rounded-control;
}

</style>
